import {type PayloadAction, createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {deleteConfiguration, downloadZip, fetchList} from "./listApi";
import {
  HttpStatus,
  isForbidden,
  isNotFound,
  isSuccess,
  isUnauthorized
} from "../../../infrastructure/http-status";
import {showUserMessages} from "../../message-panel/helpers";
import {errorUserMessage, successUserMessage} from "../../message-panel/actions";
import {
  SortDirection,
  SortStrategy,
  configurationDoesntExist,
  deleteFailed,
  deleteSuccess,
  downloadFailed,
  downloadSuccess
} from "./constants";
import {listLoadFailedError} from "../constants";
import {saveZipToDrive} from "./helpers";
import {type ListState} from "./types";
import {Status} from "../../../infrastructure/status";
import {type WithState} from "../../../store/utils";
import {displayUserMessage} from "../../message-panel/messagesSlice";

const initialState: ListState = {
  configurations: [],
  status: Status.IDLE,
  downloadStatus: Status.IDLE,
  noAccess: false,
  deleteStatus: Status.IDLE,
  searchQuery: "",
  sortStrategy: SortStrategy.NONE,
  sortDirection: SortDirection.NONE
};

export const fetchListAsync = createAsyncThunk(
  "list/fetchList",
  async (_, {fulfillWithValue, rejectWithValue, dispatch}) => {
    try {
      const response = await fetchList();

      showUserMessages(response, dispatch);

      !isSuccess(response.status) &&
        dispatch(displayUserMessage(errorUserMessage(listLoadFailedError).payload));

      if (isForbidden(response.status)) {
        return rejectWithValue({status: HttpStatus.Forbidden});
      }

      return fulfillWithValue(response.data);
    } catch (err) {
      !isForbidden(err?.response?.status) &&
        dispatch(displayUserMessage(errorUserMessage(listLoadFailedError).payload));

      return rejectWithValue({status: err?.response?.status});
    }
  }
);

export const downloadZipAsync = createAsyncThunk<VoidFunction, number>(
  "list/downloadZip",
  async (id: number, {fulfillWithValue, rejectWithValue, dispatch}) => {
    try {
      const response = await downloadZip(id);

      if (!isSuccess(response.status)) {
        isNotFound(response.status) && dispatch(errorUserMessage(configurationDoesntExist(id)));
        isUnauthorized(response.status) && dispatch(errorUserMessage(downloadFailed));

        return rejectWithValue({status: response.status});
      }

      saveZipToDrive(response);

      dispatch(successUserMessage(downloadSuccess));

      return fulfillWithValue(response.data);
    } catch (err) {
      isNotFound(err?.response?.status) && dispatch(errorUserMessage(configurationDoesntExist(id)));
      isUnauthorized(err?.response?.status) && dispatch(errorUserMessage(downloadFailed));

      return rejectWithValue({status: err?.response?.status});
    }
  }
);

export const deleteConfigurationAsync = createAsyncThunk(
  "list/deleteConfiguration",
  async (id: number, {fulfillWithValue, rejectWithValue, dispatch}) => {
    try {
      const response = await deleteConfiguration(id);

      if (!isSuccess(response.status)) {
        isNotFound(response.status) && dispatch(errorUserMessage(configurationDoesntExist(id)));
        isUnauthorized(response.status) && dispatch(errorUserMessage(deleteFailed));

        return rejectWithValue({status: response.status});
      }

      dispatch(successUserMessage(deleteSuccess));

      return fulfillWithValue(response.data);
    } catch (err) {
      isNotFound(err?.response?.status) && dispatch(errorUserMessage(configurationDoesntExist(id)));
      isUnauthorized(err?.response?.status) && dispatch(errorUserMessage(deleteFailed));

      return rejectWithValue({status: err?.response?.status});
    }
  }
);

export const listSlice = createSlice({
  name: "list",
  initialState,
  reducers: {
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
    },
    setSortStrategy(state, action: PayloadAction<SortStrategy>) {
      state.sortStrategy = action.payload;
    },
    setSortDirection(state, action: PayloadAction<SortDirection>) {
      state.sortDirection = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchListAsync.pending, state => {
        state.status = Status.LOADING;
      })
      .addCase(fetchListAsync.fulfilled, (state, action) => {
        state.status = Status.IDLE;
        state.configurations = action.payload.data;
        state.noAccess = false;
      })
      .addCase(fetchListAsync.rejected, (state, action) => {
        state.status = Status.FAILED;
        state.noAccess = isForbidden((action?.payload as {status: number})?.status);
        state.configurations = [];
      });

    builder
      .addCase(deleteConfigurationAsync.pending, state => {
        state.deleteStatus = Status.LOADING;
      })
      .addCase(deleteConfigurationAsync.fulfilled, state => {
        state.deleteStatus = Status.IDLE;
        state.noAccess = false;
      })
      .addCase(deleteConfigurationAsync.rejected, (state, action) => {
        state.deleteStatus = Status.FAILED;
        state.configurations = isForbidden((action?.payload as {status: number})?.status)
          ? []
          : state.configurations;
        state.noAccess = isForbidden((action?.payload as {status: number})?.status);
      });

    builder
      .addCase(downloadZipAsync.pending, (state, action) => {
        state.downloadStatus = Status.LOADING;
        state.downloadId = action.meta.arg;
      })
      .addCase(downloadZipAsync.fulfilled, state => {
        state.downloadStatus = Status.IDLE;
        state.noAccess = false;
      })
      .addCase(downloadZipAsync.rejected, (state, action) => {
        state.downloadStatus = Status.FAILED;
        state.configurations = isForbidden((action?.payload as {status: number})?.status)
          ? []
          : state.configurations;
        state.noAccess = isForbidden((action?.payload as {status: number})?.status);
      });
  }
});

export const selectConfigurations = (state: WithState<ListState, "list">) =>
  state.list.configurations;
export const selectStatus = (state: WithState<ListState, "list">) => state.list.status;
export const selectDownloadStatus = (state: WithState<ListState, "list">) =>
  state.list.downloadStatus;
export const selectDownloadId = (state: WithState<ListState, "list">) => state.list.downloadId;
export const selectNoAccess = (state: WithState<ListState, "list">) => state.list.noAccess;
export const selectDeleteStatus = (state: WithState<ListState, "list">) => state.list.deleteStatus;
export const selectSearchQuery = (state: WithState<ListState, "list">) => state.list.searchQuery;
export const selectSortStrategy = (state: WithState<ListState, "list">) => state.list.sortStrategy;
export const selectSortDirection = (state: WithState<ListState, "list">) =>
  state.list.sortDirection;

export const {setSearchQuery, setSortStrategy, setSortDirection} = listSlice.actions;

export const listReducer = listSlice.reducer;
